import { useEffect, useMemo, useState } from 'react';

import {
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import { useMe } from '@crehana/auth';
import { Skeleton } from '@crehana/compass.ui/skeleton';
import { Paginator, TextsDropdown } from '@crehana/compass.ui/v2';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { boldFormatter } from '@crehana/i18n';
import { useIsRenderedFromWebView } from '@crehana/react-hooks/use-is-rendered-from-webview';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Container } from '@crehana/ui';

import { Diplomas, type DiplomasProps } from '@/shared/components';
import Breadcrumb from '@/views/LearnDashboard/components/Breadcrumb';
import { useLearnDashboardContext } from '@/views/LearnDashboard/context';

import { LEARN_DASHBOARD_PAGE_NAME } from '../../../constants';
import { useCrehanaTranslation } from '../../../hooks/useCrehanaTranslation';
import {
  ENROLLMENTS_DEGREES_ROUTE,
  PROJECTS_ROUTE,
} from '../../../routes/common.routes';
import { DIPLOMA_FILTER_COLLABORATOR } from './constants';
import { useGetListCertificatesCollaborator } from './hooks/use-get-list-certificates-collaborator';
import type { LearningUserListCertificatesQueryQueryVariables } from './hooks/use-get-list-certificates-collaborator/get-list-certificates-collaborator-query.v5.generated';
import { useAddI18nLocales, useTranslation } from './i18n';
import type { TFilterDiplomasCollaboratorOption } from './type';

const DiplomasPageNew: React.FC<DiplomasProps> = () => {
  useAddI18nLocales();

  const { logEvent } = useAmplitude();
  const { me } = useMe();
  const { t } = useTranslation();
  const { t: tGlobal } = useCrehanaTranslation();
  const { isLoadingPlayroomActivated, isPlayroomEnabled } =
    useLearnDashboardContext();
  const isFromApps = useIsRenderedFromWebView();
  const { flagValue: isLiveSessionCertificateEnabled } = useFeatureFlag(
    'LEARNING_CERTIFICATION_AVAILABLE_IN_LIVE_SESSION',
  );

  const isB2B = !!me?.isB2B;
  const organizationSlug = me?.organization?.slug;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectAct, setSelectAct] =
    useState<TFilterDiplomasCollaboratorOption>('ALL');

  const {
    listCertificatesCollaborator,
    certificatesMetadata,
    loading: loadingCertificatesCollaborator,
    error: errorCertificatesCollaborator,
    refetch: refetchCertificatesCollaborator,
    totalPages,
  } = useGetListCertificatesCollaborator({
    filterByTypeCertificate: selectAct,
    currentPage,
  });

  const optionsFilterCertificates: {
    label: string;
    value: TFilterDiplomasCollaboratorOption;
  }[] = useMemo(
    () => [
      {
        label: t('DOWNLOAD_FILTER_ALL', {
          interpolation: {
            count:
              (certificatesMetadata?.total_certificates || 0) +
              (certificatesMetadata?.total_participation_certificates || 0),
          },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.ALL,
      },
      {
        label: t('DOWNLOAD_FILTER_CERTIFICATES_COURSES', {
          interpolation: {
            count: certificatesMetadata?.total_course_certificates || 0,
          },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.CERTIFICATIONS_COURSE,
      },
      {
        label: t('DOWNLOAD_FILTER_CERTIFICATES_TRACKS', {
          interpolation: {
            count: certificatesMetadata?.total_track_certificates || 0,
          },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.CERTIFICATIONS_TRACKS,
      },
      {
        label: t('DOWNLOAD_FILTER_PROOF', {
          interpolation: {
            count: certificatesMetadata?.total_participation_certificates || 0,
          },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.PARTICIPANTS_CERTIFICATIONS,
      },
      ...(isLiveSessionCertificateEnabled
        ? [
            {
              label: t('DOWNLOAD_FILTER_CERTIFICATES_LIVE_SESSIONS', {
                interpolation: {
                  count:
                    certificatesMetadata?.total_live_session_certificates || 0,
                },
              }),
              value: DIPLOMA_FILTER_COLLABORATOR.CERTIFICATIONS_LIVE_SESSION,
            },
          ]
        : []),
    ],
    [t, certificatesMetadata, isLiveSessionCertificateEnabled],
  );

  const onRefetch = (
    refetchVariables?: LearningUserListCertificatesQueryQueryVariables,
  ) => {
    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (refetchCertificatesCollaborator)
      return refetchCertificatesCollaborator(refetchVariables);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (!isLoadingPlayroomActivated) {
      logEvent({
        event: 'Render Home Certificates',
        data: {
          ...getSourceEventParams(),
          ...getCommonAmplitudeProperties(me!, isPlayroomEnabled),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPlayroomActivated, isPlayroomEnabled]);

  return (
    <>
      {!isFromApps && (
        <Breadcrumb
          items={[
            { label: tGlobal('LABEL_SIDEBAR_ITEM_MY_COURSES_CERTIFICATIONS') },
          ]}
        />
      )}
      <Container className="tw-my-2.4" type="dashboard">
        {loadingCertificatesCollaborator ? (
          <Skeleton className="tw-mb-2 tw-w-[30%] tw-h-2" />
        ) : (
          <h3 className="tw-mb-2.4 tw-font-body3 tw-text-neutral-light-800 dark:tw-text-neutral-light-100">
            {t('DOWNLOAD_DESCRIPTION_GENERAL', {
              formatter: boldFormatter,
              interpolation: {
                diplomasCount: certificatesMetadata?.total_certificates || 0,
                certificateCount:
                  certificatesMetadata?.total_participation_certificates || 0,
              },
            })}
          </h3>
        )}
        <div className="tw-flex tw-flex-row tw-justify-start tw-items-center tw-gap-1">
          <p className="tw-font-subtitle5 tw-font-semibold">
            {t('TYPE_CERTIFICATE')}
          </p>
          <TextsDropdown
            selected={selectAct}
            className="tw-max-w-24 tw-bg-neutral-light-100 dark:tw-bg-transparent"
            hideSearch
            placeholder={t('DOWNLOAD_FILTER')}
            options={optionsFilterCertificates}
            onSelect={e => {
              setCurrentPage(1);
              setSelectAct(e);
            }}
          />
        </div>
      </Container>

      <Diplomas
        amplitudePageName={LEARN_DASHBOARD_PAGE_NAME}
        enrollmentsRoutePath={
          isB2B ? `/org/${organizationSlug}/learn/` : '/learn/'
        }
        enrollmentsDegreesRoutePath={ENROLLMENTS_DEGREES_ROUTE.path}
        projectRoutePath={PROJECTS_ROUTE.path}
        loading={loadingCertificatesCollaborator}
        refetch={onRefetch}
        error={errorCertificatesCollaborator}
        actData={listCertificatesCollaborator}
      />
      <div className="tw-flex tw-justify-center tw-items-center tw-my-3.2">
        <Paginator
          variant="numeric"
          totalPages={totalPages || 0}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </>
  );
};

export default DiplomasPageNew;
